import dayjs from "dayjs";

/**
 * Az összepontozó kosár egy tétele — alakja a pontozás-szerkesztő (GeneralLedgerMatchingEditor)
 * tartozik/követel tábláinak sor-alakját követi (BookReconItemDTO), hogy a kosár tartalma
 * átalakítás nélkül tölthető legyen a szerkesztőbe.
 */
export interface ReconBasketItem {
  id: number;
  trans: number;
  accno: string;
  total: number;
  /** yyyy-MM-dd */
  accDate: string;
  accEvent: string;
  /** true = tartozik ('+'), false = követel ('-') */
  accSign: boolean;
  partnerName: string;
  remark: string;
  /** ixboktrans.recon_head (fksz-es pontozás), 1 = nincs */
  reconHead: number;
  /** ixboktrans.recon_head2 (sima pontozás), 1 = nincs */
  reconHead2: number;
}

/**
 * A tranzakció-böngésző szűrőinek pillanatképe az összepontozás-mód indítása ELŐTT — kilépéskor
 * ezt állítjuk vissza. A mezők az ApiSearchPageForLedger panel-szűrőinek felelnek meg.
 */
export interface ReconModeFilterSnapshot {
  selectedTransStats: any[];
  startDate: string;
  endDate: string;
  accno: string;
  checkType: string;
  typeManual: boolean;
  typeAuto: boolean;
}

/**
 * Összepontozás-mód állapota: a böngésző a választott évre + könyvelt státuszra (+ opcionális
 * főkönyvi számra) szűr, piros capsule jelzi. A snapshot a kilépés utáni visszaállításhoz
 * akkor is megmarad, ha a mód a böngészőn KÍVÜL (a szerkesztő mentése) ér véget — a böngésző
 * a következő megnyitásnál fogyasztja el.
 */
export interface ReconMode {
  active: boolean;
  year: number | null;
  accno: string;
  snapshot: ReconModeFilterSnapshot | null;
}

/**
 * Folytatott összepontozás: a kosár egy MEGLÉVŐ, feldolgozás alatti hxbokrecon csoport tételeivel
 * indult (kosár-menü "Folytatás" választó). A Véglegesítés ekkor a csoport szerkesztőjét nyitja, a
 * mentés a meglévő fejet frissíti. A kosár ürítése a folytatást is elengedi.
 */
export interface ReconContinuation {
  id: number;
  code: string;
  remark: string;
  /** "-" = sima pontozás */
  accno: string;
  year: number;
}

const emptyMode = (): ReconMode => ({ active: false, year: null, accno: "", snapshot: null });

/**
 * Összepontozó kosár a tranzakció-böngészőhöz: a lenyitott tétel-sorokból gyűjti a pontozandó
 * tételeket, a "Véglegesítés" a pontozás-szerkesztőbe adja át. A kosár NEM ment — egyetlen
 * mentési út van, a szerkesztőé. A tartalom csak memóriában (singleton) él: lapozást, fej-nyitást
 * és a szerkesztőbe/vissza navigálást túléli, az oldal frissítése (F5) a kosarat és a módot is
 * törli — ugyanúgy, ahogy a böngésző saját szűrőit.
 */
export class ReconBasketStore {
  items: ReconBasketItem[] = [];
  mode: ReconMode = emptyMode();
  continued: ReconContinuation | null = null;

  /**
   * A kosár éve: aktív módban a választott év, egyébként az első betett tétel könyvelési
   * dátumából rögzül; üres kosárnál null.
   */
  get year(): number | null {
    if (this.mode.active && this.mode.year) return this.mode.year;
    if (this.continued) return this.continued.year;
    return this.items.length ? dayjs(this.items[0].accDate).year() : null;
  }

  get positiveItems(): ReconBasketItem[] {
    return this.items.filter(i => i.accSign);
  }

  get negativeItems(): ReconBasketItem[] {
    return this.items.filter(i => !i.accSign);
  }

  get positiveTotal(): number {
    return this.positiveItems.reduce((sum, i) => sum + Number(i.total || 0), 0);
  }

  get negativeTotal(): number {
    return this.negativeItems.reduce((sum, i) => sum + Number(i.total || 0), 0);
  }

  get balance(): number {
    return this.positiveTotal - this.negativeTotal;
  }

  /** Ha a kosár minden tétele ugyanazon a főkönyvi számon van, az a szám — a fksz-es mód ajánlásához. */
  get commonAccno(): string | null {
    if (!this.items.length) return null;
    const first = this.items[0].accno;
    return this.items.every(i => i.accno === first) ? first : null;
  }

  has(id: number): boolean {
    return this.items.some(i => i.id === id);
  }

  /**
   * Betehető-e a tétel: üres szöveg = igen, egyébként a tiltás oka (tooltipbe).
   * Csak könyvelt (status=5) fej tétele pontozható — ugyanaz a szabály, mint a pontozás
   * tétel-választójában és a K2D d_g_tetel_valaszt-ban; az év a kosár évéhez kötött.
   */
  rejectReason(item: ReconBasketItem, headStatus: number, headStorno = false, isOnlyElabe = false): string {
    if (Number(headStatus) !== 5) {
      return "Csak könyvekben szereplő tranzakció tétele tehető a kosárba";
    }
    // K2D d_g_tetel_valaszt: sztornózott fej tétele nem pontozható, kivéve a "csak ELÁBÉ" tranzakciót
    if (headStorno && !isOnlyElabe) {
      return "Sztornózott tranzakció tétele nem pontozható";
    }
    const itemYear = dayjs(item.accDate).year();
    if (this.year !== null && itemYear !== this.year) {
      return `A kosár éve ${this.year}, ez a tétel ${itemYear}. évi`;
    }
    return "";
  }

  add(item: ReconBasketItem): void {
    if (this.has(item.id)) return;
    this.items.push(item);
  }

  remove(id: number): void {
    const idx = this.items.findIndex(i => i.id === id);
    if (idx === -1) return;
    this.items.splice(idx, 1);
  }

  clear(): void {
    this.items.splice(0, this.items.length);
    this.continued = null;
  }

  /**
   * Meglévő csoport folytatása: a kosár tartalma a csoport tételeire cserélődik (a BookReconItemDTO
   * sor-alak a kosár-tétel alakjával egyezik; a recon_head/recon_head2 a csoport módjából adódik).
   */
  loadFromRecon(head: ReconContinuation, items: Omit<ReconBasketItem, "reconHead" | "reconHead2">[]): void {
    const withAccno = !!head.accno && head.accno.trim() !== "-";
    this.items.splice(0, this.items.length);
    items.forEach(i => this.items.push({
      ...i,
      total: Number(i.total || 0),
      reconHead: withAccno ? head.id : 1,
      reconHead2: withAccno ? 1 : head.id,
    }));
    this.continued = { ...head, accno: withAccno ? head.accno.trim() : "-" };
  }

  // ---- Összepontozás-mód ----

  /** Mód indítása: a böngésző szűrőinek pillanatképe a visszaállításhoz eltárolódik. */
  startMode(year: number, accno: string, snapshot: ReconModeFilterSnapshot): void {
    this.mode.active = true;
    this.mode.year = year;
    this.mode.accno = accno || "";
    this.mode.snapshot = snapshot;
  }

  /** Mód vége: a snapshot MEGMARAD, amíg a böngésző el nem fogyasztja (takeSnapshot). */
  endMode(): void {
    this.mode.active = false;
    this.mode.year = null;
    this.mode.accno = "";
  }

  /** A visszaállítandó szűrő-pillanatkép egyszeri kiolvasása (inaktív módnál). */
  takeSnapshot(): ReconModeFilterSnapshot | null {
    if (this.mode.active || !this.mode.snapshot) return null;
    const snapshot = this.mode.snapshot;
    this.mode.snapshot = null;
    return snapshot;
  }
}

const store = new ReconBasketStore();
export default store;
